/**
 * Normalizacion de los issues de Zod que dejaron un payload en cuarentena.
 *
 * `quarantine.zod_issues` es jsonb: guarda `error.issues` tal cual lo produjo la
 * validacion del borde, y su forma puede variar entre versiones de Zod. Por eso
 * se lee de forma tolerante: lo que se entiende se muestra estructurado y lo que
 * no, se muestra como JSON crudo. Nunca se descarta: es la evidencia de por que
 * se rechazo el mensaje.
 */
import { z } from 'zod';
import type { QuarantineIssue } from '../api/types';

const rawIssueSchema = z
  .object({
    path: z.array(z.union([z.string(), z.number()])).optional(),
    code: z.string().optional(),
    message: z.string().optional(),
    expected: z.unknown().optional(),
    received: z.unknown().optional(),
  })
  .passthrough();

/** Acepta el arreglo pelado o envuelto en `{ issues: [...] }`. */
const rawIssuesSchema = z.union([
  z.array(rawIssueSchema),
  z.object({ issues: z.array(rawIssueSchema) }),
]);

/** `Movimiento.Documento[0].Cantidad`; `(raíz)` cuando el issue es del objeto entero. */
export function formatIssuePath(path: readonly (string | number)[]): string {
  if (path.length === 0) {
    return '(raíz)';
  }
  return path.reduce<string>((accumulator, segment) => {
    if (typeof segment === 'number') {
      return `${accumulator}[${segment}]`;
    }
    return accumulator === '' ? segment : `${accumulator}.${segment}`;
  }, '');
}

function describeValue(value: unknown): string | null {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Devuelve los issues listos para la tabla de cuarentena.
 *
 * Un arreglo vacio significa "no se pudo interpretar": la pantalla cae al panel
 * de JSON crudo en vez de decir que no hubo issues.
 */
export function normalizeZodIssues(value: unknown): readonly QuarantineIssue[] {
  const parsed = rawIssuesSchema.safeParse(value);
  if (!parsed.success) {
    return [];
  }
  const issues = Array.isArray(parsed.data) ? parsed.data : parsed.data.issues;
  return issues.map((issue) => ({
    path: formatIssuePath(issue.path ?? []),
    code: issue.code ?? 'invalid',
    message: issue.message ?? 'Sin mensaje de validacion.',
    expected: describeValue(issue.expected),
    received: describeValue(issue.received),
  }));
}
